import {
  OrganizationManager,
  VolunteerOrganization as VolunteerOrganizationEntity,
} from "@entities";
import type { VolunteerOrganization } from "@entities";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  ListItemText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import {
  organizationManagerRepo,
  volunteerOrgActivityDomainRepo,
  volunteerOrgProjectRepo,
} from "@repositories";
import { normalizePhoneDigitsForSearch } from "@shared/helpers/normalize-phone-for-search";
import {
  VOLUNTEER_ORG_EVENT_TYPES,
  volunteerOrgEffectiveEventWhere,
  volunteerOrgKindFilter,
} from "@shared/helpers/volunteer-organization";
import type { HyperTableFilter } from "@shared/frontend/hyper-table";
import { useCallback, useEffect, useState } from "react";
import { FormGoogle } from "../../../components/form-components/form-google-autocomplete/FormGoogle";
import { BasicButton } from "../../../components/generic-components/button/Button";

const KIND_OPTIONS = [
  { value: "independent", label: "ארגון עצמאי" },
  { value: "parent", label: "ארגון אב" },
  { value: "branch", label: "שלוחה" },
] as const;

const PHONE_FIELDS = [
  "headPhone",
  "volunteeringCoordinatorPhone",
  "emergencyContactPhone",
  "extraContactPhone",
] as const;

function filterBoxSx(minWidth: number) {
  return {
    display: "flex",
    flexDirection: "column",
    gap: "0.75rem",
    paddingInline: "0.5rem",
    minWidth,
  } as const;
}

export const VOLUNTEER_ORGANIZATION_TABLE_FILTERS: HyperTableFilter<VolunteerOrganization>[] =
  [
    {
      id: "name",
      icon: "string",
      caption: "שם",
      renderFilter: ({ onSave, meta }) => {
        const text = (meta.text as string) ?? "";
        return (
          <Box sx={filterBoxSx(220)}>
            <TextField
              size="small"
              label="שם שלוחה או ארגון אב"
              defaultValue={text}
              onBlur={(event) => {
                const query = event.target.value.trim();
                onSave({
                  value: query
                    ? VolunteerOrganizationEntity.hyperFilter({
                        $or: [
                          { name: { $contains: query } },
                          { parent: { $is: { name: { $contains: query } } } },
                        ],
                      })
                    : {},
                  meta: { text: query },
                });
              }}
            />
          </Box>
        );
      },
    },
    {
      id: "parentName",
      icon: "string",
      caption: "ארגון אב",
      renderFilter: ({ onSave, meta }) => {
        const text = (meta.text as string) ?? "";
        return (
          <Box sx={filterBoxSx(220)}>
            <TextField
              size="small"
              label="שם הארגון האב"
              defaultValue={text}
              onBlur={(event) => {
                const query = event.target.value.trim();
                onSave({
                  value: query
                    ? VolunteerOrganizationEntity.hyperFilter({
                        parent: { $is: { name: { $contains: query } } },
                      })
                    : {},
                  meta: { text: query },
                });
              }}
            />
          </Box>
        );
      },
    },
    {
      id: "handlingManager",
      icon: "string",
      caption: "מנהל מלווה",
      renderFilter: ({ onSave, meta }) => {
        const [selectedManagers, setSelectedManagers] = useState<
          OrganizationManager[]
        >((meta.selectedManagers as OrganizationManager[]) ?? []);
        const [options, setOptions] = useState<OrganizationManager[]>([]);
        const [search, setSearch] = useState("");

        const searchManagers = useCallback(async (query: string) => {
          const q = query.trim();
          if (q.length < 2) {
            setOptions([]);
            return;
          }
          const phoneQuery = normalizePhoneDigitsForSearch(q);
          const list = await organizationManagerRepo.find({
            where: OrganizationManager.hyperFilter({
              $or: [
                { name: { $contains: q } },
                { phoneNumber: { $contains: phoneQuery } },
              ],
            }),
            include: { organization: true },
            orderBy: { name: "asc" },
            limit: 25,
          });
          setOptions(list);
        }, []);

        useEffect(() => {
          const handle = window.setTimeout(() => {
            void searchManagers(search);
          }, 250);
          return () => window.clearTimeout(handle);
        }, [search, searchManagers]);

        return (
          <Box sx={filterBoxSx(280)}>
            <Autocomplete
              multiple
              options={options}
              value={selectedManagers}
              onChange={(_, value) => setSelectedManagers(value)}
              inputValue={search}
              onInputChange={(_, v) => setSearch(v)}
              getOptionLabel={(m) =>
                `${m.name}${m.organization?.name ? ` · ${m.organization.name}` : ""}`
              }
              isOptionEqualToValue={(a, b) => a.id === b.id}
              noOptionsText="הקלד לפחות 2 תווים"
              renderTags={(value, getTagProps) =>
                value.map((m, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={m.id}
                    label={m.name}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="חיפוש מנהל מלווה"
                  size="small"
                />
              )}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  const managerIds = selectedManagers.map((m) => m.id);
                  onSave({
                    value: managerIds.length
                      ? VolunteerOrganizationEntity.hyperFilter({
                          $or: [
                            { handlingManagerId: { $in: managerIds } },
                            {
                              parent: {
                                $is: { handlingManagerId: { $in: managerIds } },
                              },
                            },
                          ],
                        })
                      : {},
                    meta: {
                      selectedManagers,
                      text: selectedManagers.map((m) => m.name).join(", "),
                    },
                  });
                }}
              >
                שמירה
              </Button>
            </Box>
          </Box>
        );
      },
    },
    {
      id: "kind",
      icon: "literal",
      caption: "סוג",
      renderFilter: ({ onSave, meta }) => {
        const selected = (meta.kind as string) ?? "";
        return (
          <Box sx={filterBoxSx(200)}>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={selected}
              onChange={(event) => {
                const next = event.target.value as string;
                const kind =
                  next === "independent" || next === "parent" || next === "branch"
                    ? next
                    : null;
                onSave({
                  value: kind
                    ? VolunteerOrganizationEntity.hyperFilter(
                        volunteerOrgKindFilter(kind),
                      )
                    : {},
                  meta: {
                    kind: next,
                    text:
                      KIND_OPTIONS.find((option) => option.value === next)
                        ?.label ?? "",
                  },
                });
              }}
            >
              <MenuItem value="">הכל</MenuItem>
              {KIND_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      },
    },
    {
      id: "address",
      icon: "string",
      caption: "כתובת",
      renderFilter: ({ onSave, meta }) => {
        const text = (meta.text as string) ?? "";
        return (
          <Box sx={filterBoxSx(220)}>
            <TextField
              size="small"
              label="כתובת"
              defaultValue={text}
              onBlur={(event) => {
                const query = event.target.value.trim();
                onSave({
                  value: query
                    ? VolunteerOrganizationEntity.hyperFilter({
                        address: { $contains: query },
                      })
                    : {},
                  meta: { text: query },
                });
              }}
            />
          </Box>
        );
      },
    },
    {
      id: "isAddressInRadiusFromPoint",
      icon: "string",
      caption: "מרחק מכתובת",
      renderFilter: ({ onSave, meta }) => {
        const filterObj = {
          location: meta?.location || "",
          distanceKm: meta?.distanceKm || 0,
          latitude: meta?.latitude || 0,
          longitude: meta?.longitude || 0,
        };

        const [state, setState] = useState(filterObj);

        return (
          <Box sx={filterBoxSx(280)}>
            <FormGoogle
              label="מיקום"
              placeValue={state.location as string}
              dir="rtl"
              onLocationChange={(location) => {
                setState({
                  ...state,
                  location: location.locationName,
                  latitude: location.latLng.lat,
                  longitude: location.latLng.lng,
                });
              }}
            />
            <TextField
              type="number"
              label="מרחק בקילומטרים"
              value={state.distanceKm}
              onChange={(event) =>
                setState({
                  ...state,
                  distanceKm: Number(event.target.value) || "",
                })
              }
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <BasicButton
                style={{
                  borderRadius: "10px",
                  backgroundColor: "#05204c",
                  padding: "0.3rem 1rem",
                  fontWeight: 400,
                  fontSize: "small",
                }}
                text="שמירה"
                onClick={() => {
                  const km = Number(state.distanceKm);
                  const lat = Number(state.latitude);
                  const lng = Number(state.longitude);
                  onSave({
                    value:
                      km > 0 && (lat !== 0 || lng !== 0)
                        ? VolunteerOrganizationEntity.isAddressInRadiusFromPoint(
                            {
                              radius: {
                                distance: km,
                                measure: "km",
                              },
                              lat,
                              lng,
                            },
                          )
                        : {},
                    meta: {
                      ...state,
                      text:
                        state.location && km
                          ? `${state.location} · ${km} ק"מ`
                          : "",
                    },
                  });
                }}
              />
            </Box>
          </Box>
        );
      },
    },
    {
      id: "phones",
      icon: "string",
      caption: "טלפון",
      renderFilter: ({ onSave, meta }) => {
        const text = (meta.text as string) ?? "";
        return (
          <Box sx={filterBoxSx(220)}>
            <TextField
              size="small"
              label="טלפון"
              defaultValue={text}
              onBlur={(event) => {
                const query = event.target.value.trim();
                const digits = normalizePhoneDigitsForSearch(query);
                const term = digits || query;
                onSave({
                  value: term
                    ? VolunteerOrganizationEntity.hyperFilter({
                        $or: [
                          ...PHONE_FIELDS.map((field) => ({
                            [field]: { $contains: term },
                          })),
                          ...PHONE_FIELDS.map((field) => ({
                            parent: {
                              $is: { [field]: { $contains: term } },
                            },
                          })),
                          {
                            contacts: {
                              $some: { phone: { $contains: term } },
                            },
                          },
                          {
                            parent: {
                              $is: {
                                contacts: {
                                  $some: { phone: { $contains: term } },
                                },
                              },
                            },
                          },
                        ],
                      })
                    : {},
                  meta: { text: query },
                });
              }}
            />
          </Box>
        );
      },
    },
    {
      id: "eventTypes",
      icon: "literal",
      caption: "סוג אירוע",
      renderFilter: ({ onSave, meta }) => {
        const selected = (meta.selectedTypes as string[]) ?? [];
        return (
          <Box sx={filterBoxSx(240)}>
            <Select
              multiple
              fullWidth
              size="small"
              value={selected}
              onChange={(event) => {
                const next = event.target.value as string[];
                const where = volunteerOrgEffectiveEventWhere(
                  next.filter(
                    (id): id is (typeof VOLUNTEER_ORG_EVENT_TYPES)[number]["id"] =>
                      VOLUNTEER_ORG_EVENT_TYPES.some((item) => item.id === id),
                  ),
                );
                onSave({
                  value: where
                    ? VolunteerOrganizationEntity.hyperFilter(where)
                    : {},
                  meta: {
                    selectedTypes: next,
                    text: next
                      .map(
                        (id) =>
                          VOLUNTEER_ORG_EVENT_TYPES.find((item) => item.id === id)
                            ?.label ?? id,
                      )
                      .join(", "),
                  },
                });
              }}
              renderValue={(value) =>
                (value as string[]).length
                  ? (value as string[])
                      .map(
                        (id) =>
                          VOLUNTEER_ORG_EVENT_TYPES.find((item) => item.id === id)
                            ?.label ?? id,
                      )
                      .join(", ")
                  : "בחרו סוג אירוע"
              }
            >
              {VOLUNTEER_ORG_EVENT_TYPES.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox checked={selected.includes(item.id)} />
                  <ListItemText primary={item.label} />
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      },
    },
    {
      id: "domains",
      icon: "literal",
      caption: "סוג עשייה",
      renderFilter: ({ onSave, meta }) => {
        const selected = (meta.selectedIds as string[]) ?? [];
        const [options, setOptions] = useState<
          { id: string; name: string }[]
        >((meta.options as { id: string; name: string }[]) ?? []);

        useEffect(() => {
          void volunteerOrgActivityDomainRepo
            .find({
              orderBy: { sortOrder: "asc" },
            })
            .then((rows) => setOptions(rows.map((row) => ({ id: row.id, name: row.name }))));
        }, []);

        return (
          <Box sx={filterBoxSx(260)}>
            <Select
              multiple
              fullWidth
              size="small"
              value={selected}
              onChange={(event) => {
                const next = event.target.value as string[];
                onSave({
                  value: next.length
                    ? VolunteerOrganizationEntity.hyperFilter({
                        $or: [
                          {
                            activities: {
                              $some: { domainId: { $in: next } },
                            },
                          },
                          {
                            parent: {
                              $is: {
                                activities: {
                                  $some: { domainId: { $in: next } },
                                },
                              },
                            },
                          },
                        ],
                      })
                    : {},
                  meta: {
                    selectedIds: next,
                    options,
                    text: next
                      .map(
                        (id) =>
                          options.find((option) => option.id === id)?.name ?? id,
                      )
                      .join(", "),
                  },
                });
              }}
              renderValue={(value) =>
                (value as string[]).length
                  ? (value as string[])
                      .map(
                        (id) =>
                          options.find((option) => option.id === id)?.name ?? id,
                      )
                      .join(", ")
                  : "בחרו תחום"
              }
            >
              {options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Checkbox checked={selected.includes(option.id)} />
                  <ListItemText primary={option.name} />
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      },
    },
    {
      id: "project",
      icon: "literal",
      caption: "פרויקט",
      renderFilter: ({ onSave, meta }) => {
        const selected = (meta.selectedIds as string[]) ?? [];
        const [options, setOptions] = useState<
          { id: string; name: string }[]
        >((meta.options as { id: string; name: string }[]) ?? []);

        useEffect(() => {
          void volunteerOrgProjectRepo
            .find({
              orderBy: { name: "asc" },
            })
            .then((rows) => setOptions(rows.map((row) => ({ id: row.id, name: row.name }))));
        }, []);

        return (
          <Box sx={filterBoxSx(260)}>
            <Select
              multiple
              fullWidth
              size="small"
              value={selected}
              onChange={(event) => {
                const next = event.target.value as string[];
                onSave({
                  value: next.length
                    ? VolunteerOrganizationEntity.hyperFilter({
                        assignments: {
                          $some: { projectId: { $in: next } },
                        },
                      })
                    : {},
                  meta: {
                    selectedIds: next,
                    options,
                    text: next
                      .map(
                        (id) =>
                          options.find((option) => option.id === id)?.name ?? id,
                      )
                      .join(", "),
                  },
                });
              }}
              renderValue={(value) =>
                (value as string[]).length
                  ? (value as string[])
                      .map(
                        (id) =>
                          options.find((option) => option.id === id)?.name ?? id,
                      )
                      .join(", ")
                  : "בחרו פרויקט"
              }
            >
              {options.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  <Checkbox checked={selected.includes(option.id)} />
                  <ListItemText primary={option.name} />
                </MenuItem>
              ))}
            </Select>
          </Box>
        );
      },
    },
    {
      fieldName: "volunteerCapacity",
      id: "volunteerCapacity",
      caption: "קיבולת מתנדבים",
    },
    {
      id: "hasLocation",
      icon: "boolean",
      caption: "מיקום במפה",
      renderFilter: ({ onSave, meta }) => {
        const selected = (meta.hasLocation as string) ?? "";
        return (
          <Box sx={filterBoxSx(200)}>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={selected}
              onChange={(event) => {
                const next = event.target.value as string;
                onSave({
                  value:
                    next === "yes"
                      ? VolunteerOrganizationEntity.hyperFilter({
                          coordinates: {
                            $ne: null as unknown as { lat: number; lng: number },
                          },
                        })
                      : next === "no"
                        ? VolunteerOrganizationEntity.hyperFilter({
                            coordinates: null,
                          })
                        : {},
                  meta: {
                    hasLocation: next,
                    text:
                      next === "yes"
                        ? "עם נקודה במפה"
                        : next === "no"
                          ? "בלי נקודה במפה"
                          : "",
                  },
                });
              }}
            >
              <MenuItem value="">הכל</MenuItem>
              <MenuItem value="yes">עם נקודה במפה</MenuItem>
              <MenuItem value="no">בלי נקודה במפה</MenuItem>
            </Select>
          </Box>
        );
      },
    },
    {
      id: "activeAssignment",
      icon: "boolean",
      caption: "סטטוס הפעלה",
      renderFilter: ({ onSave, meta }) => {
        const value = (meta.value as string) ?? "";
        return (
          <Box sx={filterBoxSx(220)}>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={value}
              onChange={(event) => {
                const next = event.target.value;
                onSave({
                  value:
                    next === "yes"
                      ? VolunteerOrganizationEntity.hyperFilter({
                          assignments: {
                            $some: { project: { $is: { isActive: true } } },
                          },
                        })
                      : next === "no"
                        ? VolunteerOrganizationEntity.hyperFilter({
                            assignments: {
                              $none: { project: { $is: { isActive: true } } },
                            },
                          })
                        : {},
                  meta: {
                    value: next,
                    text:
                      next === "yes"
                        ? "משובץ לפרויקט פעיל"
                        : next === "no"
                          ? "בלי פרויקט פעיל"
                          : "",
                  },
                });
              }}
            >
              <MenuItem value="">הכל</MenuItem>
              <MenuItem value="yes">משובץ לפרויקט פעיל</MenuItem>
              <MenuItem value="no">בלי פרויקט פעיל</MenuItem>
            </Select>
          </Box>
        );
      },
    },
    {
      id: "hasCommunication",
      icon: "boolean",
      caption: "תיעוד תקשורת",
      renderFilter: ({ onSave, meta }) => {
        const value = (meta.value as string) ?? "";
        return (
          <Box sx={filterBoxSx(180)}>
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={value}
              onChange={(event) => {
                const next = event.target.value;
                onSave({
                  value:
                    next === "yes"
                      ? VolunteerOrganizationEntity.hyperFilter({
                          communicationRecords: { $some: {} },
                        })
                      : next === "no"
                        ? VolunteerOrganizationEntity.hyperFilter({
                            communicationRecords: { $none: {} },
                          })
                        : {},
                  meta: {
                    value: next,
                    text: next === "yes" ? "יש תיעוד" : next === "no" ? "אין תיעוד" : "",
                  },
                });
              }}
            >
              <MenuItem value="">הכל</MenuItem>
              <MenuItem value="yes">יש תיעוד</MenuItem>
              <MenuItem value="no">אין תיעוד</MenuItem>
            </Select>
          </Box>
        );
      },
    },
  ];
